import React from 'react';
import Scrollbars from 'react-custom-scrollbars';
import styled from 'styled-components';
import PropTypes, { shape } from 'prop-types';
import Configuration from '../models/Configuration';
import Button from '../atoms/button';
import I10n from '../features/i10n';
import { configurationStatuses } from '../constants';
import CustomButton from './custom_button';
import DynamicLabel from '../atoms/dynamic-label';
import LoadingSpinner from '../atoms/loading';
import { validateDuplicateIsland } from '../features/screens/configuration_editor_screen/ducks';

const Wrapper = styled.section`
  display: flex;
  flex-direction: column;
  height: 300px;
  background-color: rgb(247, 247, 247);
  @media (max-width: 800px) {
    display: block;
    height: auto;
  }
`;

const ListArea = styled.div`
  min-height: ${props => (props.maxExtensionRacks || 0) * 70}px;
  max-height: 250px;
  overflow: auto;
  @media (max-width: 800px) {
    display: none;
  }
`;

const ActionArea = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin-left: 10px;
  margin-right: 10px;
  @media (max-width: 800px) {
    margin-top: 20px;
    max-width: unset;
  }
`;

const MobileListArea = styled.div`
  display: none;
  @media (max-width: 800px) {
    display: block;
  }
`;

const OrderedList = styled.ol`
  padding: 0;
`;

const ListItem = styled.div`
  display: flex;
  align-items: center;
  margin-right: 10px;
  height: auto;
  width: 100%;
  flex-shrink: 1;
  @media (max-width: 800px) {
    margin-right: 0px;
  }
`;

const ListItemContent = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  width: 100%;
  flex-shrink: 1;
  @media (max-width: 800px) {
    display: none;
  }
`;

const MobileListItemContent = styled.div`
  display: none;
  @media (max-width: 800px) {
    display: flex;
    flex-direction: column;
    width: 100%;
    margin-left: 10px;
  }
`;

const ListItemAnchor = styled.div`
  display: flex;
  flex: 1 1 auto;
  flex-shrink: 1;
  overflow: hidden;
  @media (max-width: 800px) {
    flex-direction: column;
  }
`;

const MobileListItemAnchor = styled.div`
  flex: 1 1 auto;
  display: flex;
  @media (max-width: 800px) {
    flex-direction: row;
  }
`;

const ItemInfoWrapper = styled.div`
  display: flex;
  align-items: center;
  border-radius: 8px;
  background-color: #F7F7F7;
  flex-shrink: 1;
  margin-left: 20px;
  overflow: hidden;
  @media (max-width: 800px) {
    font-size: 10px;
  }
`;

const ItemInfoWrapperContainer = styled.div`
  display: flex;
  align-items: center;
  @media (max-width: 800px) {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
  }
  @media (max-width: 560px) {
    flex-direction: row;
    padding: 5px 0px;
  }
  @media (max-width: 350px) {
    flex-direction: column;
  }
`;

const ItemInfo = styled.span`
  white-space: nowrap;
  text-overflow: ellipsis;
  color: #626469;
  font-size: 14px;
  line-height: 17px;
  margin: 0 15px;
  flex-shrink: 1;
  @media (max-width: 800px) {
    font-size: 12px;
  }
`;

const RemoveAction = styled.div`
  margin-left: 8px;
`;

const RackWrapper = styled.div`
  box-shadow: 0px 0px 8px 0px rgba(0, 0, 0, 0.20);
  display: flex;
  flex-direction: column;
  align-items: stretch;
  justify-content: center;
  margin: 10px;
  min-height: 50px;
  background-color: white;
  border-radius: 0 4px 4px 0;
  border-left: 4px solid #9FA0A4;
  padding-right: 10px;
  @media (max-width: 800px) {
    flex-direction: column;
  }
`;

const DynamicLabelWrapper = styled.div`
  margin-left: ${props => props.isIsland ? 10 : 20}px;
  font-weight: bold;
  @media (max-width: 800px) {
    display: none;
  }
`;

const MobileDynamicLabelWrapper = styled.div`
  display: none;
  @media (max-width: 800px) {
    display: flex;
  }
`;

const ButtonWrapper = styled.div`
  @media (max-width: 800px) {
    font-size: 10px;
    flex-direction: column;
    margin-left: 20px;
  }
  @media (max-width: 560px) {
    margin-left: 0px;
    padding: 5px 0px;
  }
`;

const DupplicateAction = styled.div`
  margin-left: 8px;
`;

const MobileDuplicateRemoveIcon = styled.div`
  display: flex;
  flex-direction: row;
  align-content: center;
  align-items: center;
`;

const MobileContainer = styled.div`
  display: none;
  @media (max-width: 800px) {
    width: 100%;
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 5px;
  }
  @media (max-width: 560px) {
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: center;
  }
`;

const SmallLoadingSpinner = styled(LoadingSpinner)`
  width: 15px !important;
  height: 15px !important;
`;

class RemoteAreaListSection extends React.Component {
  static propTypes = {
    configuration: shape(Configuration),
    create: PropTypes.func,
    edit: PropTypes.func,
    delete: PropTypes.func,
    duplicateIsland: PropTypes.func,
    canAdd: PropTypes.bool,
    canDelete: PropTypes.bool,
    openModal: PropTypes.func,
    closeModal: PropTypes.func,
    allowSavedModifications: PropTypes.bool,
    renameIOIslandWithSlot: PropTypes.func,
    isSaving: PropTypes.bool
  };

  constructor(props) {
    super(props);
    this.state = {
      canDuplicateMap: {},
      validateDuplicationLoading: false
    };
    this._isMounted = false;
  }

  updateStateIfMounted = (newState) => {
    if (this._isMounted) {
      this.setState(newState);
    }
  }

  componentDidMount() {
    this._isMounted = true;
    this.checkIslandDuplicationValidation();
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  checkIslandDuplicationValidation() {
    const { configuration } = this.props;
    this.updateStateIfMounted({ validateDuplicationLoading: true, error: null });
    return validateDuplicateIsland(configuration.id)
      .then((payload) => {
        this.updateStateIfMounted({ canDuplicateMap: payload.canDuplicateMap, validateDuplicationLoading: false });
      })
      .catch((error) => {
        console.error('Error:', error);
        this.updateStateIfMounted({canDuplicateMap: {}, error: error.message, validateDuplicationLoading: false });
      });
  }

  handleDuplicateConfiguration = (isSaved, callback) => {
    if (isSaved && !this.props.allowSavedModifications) {
      const modalTitle = 'modal.change_saved_configuration.title';
      const modalBody = 'modal.change_saved_configuration.body';

      this.props.openModal({
        modalType: 'confirmation',
        modalProps: {
          onCancel: () => { this.props.closeModal(); },
          onSubmit: () => {
            this.props.closeModal();
            callback();
          },
          title: I10n.get(modalTitle),
          text: I10n.get(modalBody),
          cancelText: I10n.get('app.action.cancel'),
          submitText: I10n.get('app.action.continue'),
        }
      });
    } else {
      callback();
    }
  }

  duplicateRemote = (slot) => {
    this.props.duplicateRemote(slot).then(() => {
      this.checkIslandDuplicationValidation();
    }).catch((error) => {
      console.error('Error during duplication:', error);
    });
  };

  deleteRemote = (slot) => {
    this.props.delete(slot).then(() => {
      this.checkIslandDuplicationValidation();
    }).catch((error) => {
      console.error('Error during deletion:', error);
    });
  };

  openAddModal(event, config, callback) {
    const isSaved = config.status === configurationStatuses.finished;
    this.handleDuplicateConfiguration(isSaved, () => {
      if (!this.props.canAdd) {
        event.preventDefault();
        return;
      }
      this.props.openModal({
        modalType: 'add_remote_area',
        modalProps: {
          onCancel: () => { this.props.closeModal(); },
          configurationId: config.id,
          addNewRemoteArea: callback
        },
      });
    });
  }

  isComponentLoading = () => {
    return this.props.isSaving || this.state.validateDuplicationLoading;
  }

  renderIslandList = (config) => {
    const isSaved = config.status === configurationStatuses.finished;
    return (
      <OrderedList>
        {config.ioIslands.sort((a, b) => a.slot - b.slot).map((island, islIdx) => {
          const islandConfig = island.configuration;
          const key = `${island.id}-${islIdx}`;
          const isModuleHarsh = island.configuration?.rack?.ruggedization;

          let displayText = "";
          if (isModuleHarsh) {
            displayText = I10n.get(`ranges.configuration_rack_${isModuleHarsh}`);
          }

          return (
            <RackWrapper id='rackWrapper' key={key}>
              <ListItem id='ListItem'>
                <ListItemContent id='ListContent'>
                  <ListItemAnchor id='ListItemAnchor'>
                    <DynamicLabelWrapper isIsland={this.props.isIsland}>
                      <DynamicLabel
                        value={islandConfig.name}
                        setValue={(value) => this.props.renameIOIslandWithSlot(value, island.slot)}
                        enableEdit={!this.isComponentLoading()}
                        withCancel={true}
                        customInputProps={{ name: "islandName", maxLength: "20", size: "18", fontWeight: "bold" }}
                      />
                    </DynamicLabelWrapper>
                    <ItemInfoWrapperContainer id='ItemInfoWrapperContainer'>
                      <ItemInfoWrapper id='ItemInfoWrapper'>
                        <ItemInfo>{I10n.get('screen.rack_list_section.nb_devices')}{islandConfig.ioModuleCount()} / {islandConfig.rack?.maxIoModules || 0}</ItemInfo>
                      </ItemInfoWrapper>
                      <ItemInfo>
                        {displayText}
                      </ItemInfo>
                    </ItemInfoWrapperContainer>
                  </ListItemAnchor>
                  <ButtonWrapper>
                    <Button
                      option={'flat'}
                      size="standard"
                      icon-name={'arrow-left-out-rectangle'}
                      disabled={this.isComponentLoading()}
                      onClick={(event) => {
                        event.preventDefault();
                        this.props.edit(island.slot);
                      }}
                    >
                      {I10n.get('screen.rack_list_section.open_editor')}
                    </Button>
                  </ButtonWrapper>
                  <ButtonWrapper>
                  <DupplicateAction>
                    {this.isComponentLoading() ? (
                      <SmallLoadingSpinner />
                    ) : (
                      <Button
                        option={'flat'}
                        size="standard"
                        icon-name={'pages-double'}
                        disabled={this.state.canDuplicateMap[island.slot] === false}
                        onClick={(event) => {
                          event.preventDefault();
                          this.handleDuplicateConfiguration(isSaved, () => {
                            this.duplicateRemote(island.slot);
                          }, true);
                        }}
                        icon-only={true}
                      >
                      </Button>
                    )}
                  </DupplicateAction>
                  </ButtonWrapper>
                    {this.props.canDelete ? (
                      <RemoveAction id='RemoveAction'
                      title={I10n.getAndReplace('screen.config_editor.io_island.delete', islandConfig.name)}
                    >
                      {this.state?.isDeleting ? (
                        <SmallLoadingSpinner />
                      ) : (
                        <Button
                          option={'flat'}
                          size="standard"
                          icon-name={'trash-can'}
                          variant="destructive"
                          importance="destructive"
                          onClick={(event) => {
                            event.preventDefault();
                            this.handleDuplicateConfiguration(isSaved, () => {
                              this.deleteRemote(island.slot);
                            }, true);
                          }}
                          icon-only={true}
                        >
                        </Button>
                      )}
                    </RemoveAction>
                  ) : (
                    <RemoveAction></RemoveAction>
                  )}
                </ListItemContent>
                <MobileListItemContent id='MobileListContent'>
                  <MobileListItemAnchor id='MobileListItemAnchor'>
                    <MobileDynamicLabelWrapper id='MobileDynamicLabelWrapper'>
                      <DynamicLabel
                        id='DynamicLabel'
                        value={islandConfig.name}
                        setValue={(value) => this.props.renameIOIslandWithSlot(value, island.slot)}
                        enableEdit={!this.isComponentLoading()}
                        withCancel={true}
                        customInputProps={{ name: "islandName", maxLength: "20", size: "13", fontWeight: "bold" }}
                      />
                    </MobileDynamicLabelWrapper>
                  </MobileListItemAnchor>
                  <MobileContainer id='MobileContainer'>
                    <ItemInfoWrapperContainer id='ItemInfoWrapperContainer'>
                      <ItemInfoWrapper id='ItemInfoWrapper'>
                        <ItemInfo>{I10n.get('screen.rack_list_section.nb_devices')}{islandConfig.ioModuleCount()} / {islandConfig.rack?.maxIoModules || 0}</ItemInfo>
                      </ItemInfoWrapper>
                      <ItemInfo>
                        {displayText}
                      </ItemInfo>
                    </ItemInfoWrapperContainer>
                    <ButtonWrapper>
                      <Button
                        option={'flat'}
                        size="small"
                        icon-name={'arrow-left-out-rectangle'}
                        disabled={this.isComponentLoading()}
                        onClick={(event) => {
                          event.preventDefault();
                          this.props.edit(island.slot);
                        }}
                      >
                        {I10n.get('screen.rack_list_section.open_editor')}
                      </Button>
                    </ButtonWrapper>
                    <MobileDuplicateRemoveIcon>
                      <ButtonWrapper>
                        <DupplicateAction>
                          {this.isComponentLoading() ? (
                            <SmallLoadingSpinner />
                          ) : (
                            <Button
                              option={'flat'}
                              size="small"
                              icon-name={'pages-double'}
                              disabled={!this.state.canDuplicateMap[island.slot]}
                              onClick={(event) => {
                                event.preventDefault();
                                this.handleDuplicateConfiguration(isSaved, () => {
                                  this.duplicateRemote(island.slot);
                                }, true);
                              }}
                              icon-only={true}
                            >
                            </Button>
                          )}
                        </DupplicateAction>
                      </ButtonWrapper>
                      {this.props.canDelete ? (
                        <RemoveAction id='RemoveAction'
                          title={I10n.getAndReplace('screen.config_editor.io_island.delete', islandConfig.name)}
                        >
                          {this.state?.isDeleting ? (
                            <SmallLoadingSpinner />
                          ) : (
                            <Button
                              option={'flat'}
                              size="small"
                              icon-name={'trash-can'}
                              variant="destructive"
                              importance="destructive"
                              onClick={(event) => {
                                event.preventDefault();
                                this.handleDuplicateConfiguration(isSaved, () => {
                                  this.deleteRemote(island.slot);
                                }, true);
                              }}
                              icon-only={true}
                            >
                            </Button>
                          )}
                        </RemoveAction>
                      ) : (
                        <RemoveAction></RemoveAction>
                      )}
                    </MobileDuplicateRemoveIcon>
                  </MobileContainer>
                </MobileListItemContent>
              </ListItem>
            </RackWrapper>
          );
        })}
      </OrderedList>
    );
  };

  render = () => {
    const config = this.props.configuration;
    const hasIoIslands = config.ioIslands.length > 0;
    return (
      <Wrapper>
        {hasIoIslands && (
          <>
            <ListArea id='ListAera' maxExtensionRacks={Math.min(config.ioIslands.length, 3)}>
              <Scrollbars>
                {this.renderIslandList(config)}
              </Scrollbars>
            </ListArea>
            <MobileListArea>
              {this.renderIslandList(config)}
            </MobileListArea>
          </>
        )}
        {config.ioIslands.length < config.headModule?.maxRemoteArea && (
          <ActionArea id="ActionArea">
            <CustomButton primary disabled={!this.props.canAdd} onClick={(event) => this.openAddModal(event, config, this.props.create)}>
              {I10n.get('screen.config_editor.remote.initiate')}
            </CustomButton>
          </ActionArea>
        )}
      </Wrapper>
    );
  };
}

export default RemoteAreaListSection;
